import {
    fetchMeCached,
    IdentityChangedError,
    isTransientError,
} from '@/lib/api/auth/fetchers'
import { requirementsForRoute, satisfies } from '@/lib/nav/groups'
import { createRouter, createWebHistory } from 'vue-router'

const Home = () => import('./pages/Home.vue')
const Search = () => import('./pages/Search.vue')
const LiveOps = () => import('./pages/LiveOps.vue')
const Tables = () => import('./pages/Tables.vue')
const DeveloperTesting = () => import('./pages/DeveloperTesting.vue')
const IndexTestingPage = () => import('./pages/IndexTestingPage.vue')
const PayoutQueryReview = () => import('./pages/PayoutQueryReview.vue')
const Table = () => import('./pages/Table.vue')
const View = () => import('./pages/View.vue')
const Book = () => import('./pages/Book.vue')
const PreReview = () => import('./pages/PreReview.vue')
const PostReview = () => import('./pages/PostReview.vue')
const JobModeration = () => import('./pages/JobModeration.vue')
const JobModerationView = () => import('./pages/JobModerationView.vue')
const Gateway = () => import('./pages/Gateway.vue')
const Quotebuilder = () => import('./pages/Quotebuilder.vue')
const GeneralDashboard = () => import('./pages/GeneralDashboard.vue')
const OperationsDashboard = () => import('./pages/OperationsDashboard.vue')
const CustomerServiceDashboard = () =>
    import('./pages/CustomerServiceDashboard.vue')
const AutoAllocationDashboard = () =>
    import('./pages/AutoAllocationDashboard.vue')
const JobModerationDashboard = () =>
    import('./pages/JobModerationDashboard.vue')
const ImageAnalysisDashboard = () =>
    import('./pages/ImageAnalysisDashboard.vue')
const SkipsPage = () => import('./pages/SkipsPage.vue')
const SkipsMovementsPage = () => import('./pages/SkipsMovementsPage.vue')
const SkipsPurchaseOrderPage = () =>
    import('./pages/SkipsPurchaseOrderPage.vue')
const ActionsAndHandoversLayout = () =>
    import('./pages/ActionsAndHandoversLayout.vue')
const PendingActionsTable = () =>
    import('@/components/pending-actions/PendingActionsTable.vue')
const HandoversTable = () => import('@/components/handovers/HandoversTable.vue')
const PendingActionView = () => import('./pages/PendingActionView.vue')
const HandoverView = () => import('./pages/HandoverView.vue')
const TippingTicketReview = () => import('./pages/TippingTicketReview.vue')
const CommercialAccountsPage = () =>
    import('./pages/CommercialAccountsPage.vue')
const DealsPage = () => import('./pages/DealsPage.vue')
const DealDetailPage = () => import('./pages/DealDetailPage.vue')
const CommercialAccountDetailPage = () =>
    import('./pages/CommercialAccountDetailPage.vue')
const EventRelaysPage = () => import('./pages/EventRelaysPage.vue')
const WasteStreamReportPage = () => import('./pages/WasteStreamReportPage.vue')
const UiDebugPage = () => import('./pages/UiDebugPage.vue')
const UnauthorizedPage = () => import('./pages/UnauthorizedPage.vue')
const SupplyPage = () => import('./pages/Supply.vue')
const PartnerApplicationsPage = () => import('./pages/PartnerApplications.vue')
const PartnerDetailPage = () => import('./pages/PartnerDetail.vue')
const CarrierPerformancePage = () => import('./pages/CarrierPerformance.vue')
const FastTrackPartnersPage = () => import('./pages/FastTrackPartners.vue')
const FastTrackPartnerCreatePage = () =>
    import('./pages/FastTrackPartnerCreate.vue')
const AssignmentPage = () => import('./pages/AssignmentPage.vue')
const LaraKeysPage = () => import('./pages/LaraKeys.vue')
const LaraAliasesPage = () => import('./pages/LaraAliases.vue')
const LaraPromptsPage = () => import('./pages/LaraPrompts.vue')
const ImageAnalysisPage = () => import('./pages/ImageAnalysis.vue')

const routes = [
    {
        name: 'home',
        path: '/',
        component: Home,
        meta: {
            title: 'Home',
            icon: 'material-symbols:home',
            sidebarOrder: 0,
        },
    },
    {
        name: 'search',
        path: '/search/:search(.*)*',
        component: Search,
        meta: {
            title: 'Search',
            icon: 'material-symbols:search',
            sidebarOrder: 1,
        },
    },
    {
        name: 'view',
        path: '/view/:id(.*)*',
        component: View,
        meta: { title: 'View', reuseOnQueryChange: true },
    },
    {
        name: 'live-ops',
        path: '/liveops',
        component: LiveOps,
        meta: { title: 'LiveOps' },
    },
    {
        name: 'dashboard',
        path: '/dashboard',
        redirect: { name: 'dashboard-general' },
    },
    {
        name: 'dashboard-general',
        path: '/dashboards/general',
        component: GeneralDashboard,
        meta: {
            title: 'General Dashboard',
            sidebarLabel: 'General',
            icon: 'material-symbols:space-dashboard',
            sidebarGroup: 'dashboards',
            sidebarOrder: 1,
        },
    },
    {
        name: 'dashboard-operations',
        path: '/dashboards/operations',
        component: OperationsDashboard,
        meta: {
            title: 'Operations Dashboard',
            sidebarLabel: 'Fulfilment Operations',
            icon: 'material-symbols:monitoring',
            sidebarGroup: 'dashboards',
            sidebarOrder: 2,
            requiresApplication: 'beehive',
            requiresRole: ['admin', 'viewer'],
        },
    },
    {
        name: 'dashboard-customer-service',
        path: '/dashboards/customer-service',
        component: CustomerServiceDashboard,
        meta: {
            title: 'Activity Dashboard',
            sidebarLabel: 'Activity',
            icon: 'material-symbols:support-agent',
            sidebarGroup: 'dashboards',
            sidebarOrder: 3,
            requiresApplication: 'beehive',
            requiresRole: ['engineer', 'slt', 'elt'],
        },
    },
    {
        name: 'dashboard-auto-allocation',
        path: '/dashboards/auto-allocation',
        component: AutoAllocationDashboard,
        meta: {
            title: 'Auto-allocation Dashboard',
            sidebarLabel: 'Auto-allocation',
            icon: 'material-symbols:bolt',
            sidebarGroup: 'dashboards',
            sidebarOrder: 4,
            requiresApplication: 'beehive',
            requiresRole: ['engineer', 'slt', 'elt'],
        },
    },
    {
        name: 'dashboard-job-moderation',
        path: '/dashboards/job-moderation',
        component: JobModerationDashboard,
        meta: {
            title: 'Job Moderation Dashboard',
            sidebarLabel: 'Job Moderation',
            icon: 'material-symbols:mop-outline',
            sidebarGroup: 'dashboards',
            sidebarOrder: 5,
            requiresApplication: 'beehive',
            requiresRole: ['engineer', 'slt', 'elt'],
        },
    },
    {
        name: 'dashboard-image-analysis',
        path: '/dashboards/image-analysis',
        component: ImageAnalysisDashboard,
        meta: {
            title: 'Image Analysis Dashboard',
            sidebarLabel: 'Image Analysis',
            icon: 'material-symbols:photo-library',
            sidebarGroup: 'dashboards',
            sidebarOrder: 6,
            requiresApplication: 'beehive',
            requiresRole: ['engineer', 'slt', 'elt'],
        },
    },
    {
        name: 'pre-review',
        path: '/prereview/:id(.*)*',
        component: PreReview,
        meta: {
            title: 'Cleanse',
            reuseOnParamChange: true,
        },
    },
    {
        name: 'assignment',
        path: '/jobs/assignment',
        component: AssignmentPage,
        meta: {
            title: 'Assignment',
            icon: 'material-symbols:assignment',
            sidebarGroup: 'fulfilment-operations',
            sidebarOrder: 3,
            sidebarLabel: 'Assignment',
            reuseOnQueryChange: true,
        },
    },
    {
        name: 'job-moderation-view',
        path: '/job-moderation/:recordId',
        component: JobModerationView,
        meta: { title: 'Job Moderation', reuseOnQueryChange: true },
    },
    {
        name: 'job-moderation',
        path: '/job-moderation',
        component: JobModeration,
        meta: {
            reuseOnQueryChange: true,
            title: 'Job Moderation',
            icon: 'material-symbols:mop-outline',
            sidebarOrder: 4,
            requiresApplication: 'beehive',
        },
    },
    {
        name: 'post-review',
        path: '/postreview/:id(.*)*',
        component: PostReview,
        meta: {
            title: 'Job Review',
            icon: 'material-symbols:fact-check',
            sidebarGroup: 'post-job',
            sidebarOrder: 0,
            sidebarQuery: { viewFilter: 'Job Review', quickSelect: 'today' },
        },
    },
    {
        name: 'table',
        path: '/table/:tableName',
        component: Table,
        meta: { title: 'Table' },
    },
    {
        name: 'book',
        path: '/book/:trackedCustomer?/:documentId?',
        component: Book,
        meta: {
            title: 'Booking',
            icon: 'material-symbols:account-balance-wallet',
            sidebarGroup: 'sales-orders',
            sidebarOrder: 1,
            sidebarLabel: 'Account Booking',
        },
    },
    {
        name: 'skips',
        path: '/skips',
        component: SkipsPage,
        meta: {
            title: 'Skips',
            icon: 'material-symbols:list-alt',
            sidebarGroup: 'skips',
            sidebarOrder: 0,
            sidebarLabel: 'Overview',
        },
    },
    {
        name: 'skips-movements',
        path: '/skips/movements',
        component: SkipsMovementsPage,
        meta: {
            title: 'Skips Movements',
            icon: 'material-symbols:route',
            sidebarGroup: 'skips',
            sidebarOrder: 1,
            sidebarLabel: 'Movements',
        },
    },
    {
        name: 'skips-purchase-order',
        path: '/skips/purchase-order',
        component: SkipsPurchaseOrderPage,
        meta: {
            title: 'Data by Purchase Order',
            icon: 'material-symbols:receipt-long',
            sidebarGroup: 'skips',
            sidebarOrder: 2,
            sidebarLabel: 'Purchase Order',
        },
    },
    {
        path: '/pending-actions',
        component: ActionsAndHandoversLayout,
        props: true,
        children: [
            {
                path: '',
                name: 'pending-actions',
                component: PendingActionsTable,
                meta: {
                    title: 'Pending Actions',
                    icon: 'material-symbols:pending-actions',
                    sidebarGroup: 'customer-service',
                    sidebarOrder: 0,
                },
            },
            {
                path: ':actionId',
                name: 'pending-action-view',
                component: PendingActionView,
                meta: { title: 'Pending Action Details' },
            },
        ],
    },
    {
        path: '/handovers',
        component: ActionsAndHandoversLayout,
        props: true,
        children: [
            {
                path: '',
                name: 'handovers',
                component: HandoversTable,
                meta: {
                    title: 'Handovers',
                    icon: 'material-symbols:swap-horiz',
                    sidebarGroup: 'customer-service',
                    sidebarOrder: 1,
                },
            },
            {
                path: ':handoverId',
                name: 'handover-view',
                component: HandoverView,
                meta: { title: 'Handover Details' },
            },
        ],
    },
    {
        name: 'tables',
        path: '/tables',
        component: Tables,
        meta: {
            title: 'Tables',
            icon: 'material-symbols:table-chart',
            sidebarGroup: 'fulfilment-operations',
            sidebarOrder: 2,
        },
    },

    {
        name: 'gateway',
        path: '/gateway',
        component: Gateway,
        meta: {
            title: 'Gateway',
            reuseOnParamChange: true,
            icon: 'material-symbols:lan',
            sidebarGroup: 'fulfilment-operations',
            sidebarOrder: 1,
        },
    },
    {
        name: 'quotebuilder',
        path: '/quotebuilder/:id(.*)*',
        component: Quotebuilder,
        meta: {
            title: 'Quote Builder',
            icon: 'material-symbols:build',
            sidebarGroup: 'sales-orders',
            sidebarOrder: 0,
        },
    },
    {
        name: 'payoutQueryReview',
        path: '/query-review',
        component: PayoutQueryReview,
        meta: { title: 'Payout Query Review' },
    },
    {
        name: 'tippingTicketReview',
        path: '/tipping-ticket-review',
        component: TippingTicketReview,
        meta: {
            title: 'Tipping Ticket Review',
            icon: 'material-symbols:confirmation-number',
            sidebarGroup: 'post-job',
            sidebarOrder: 1,
        },
    },
    {
        name: 'commercial-accounts',
        path: '/commercial-accounts',
        component: CommercialAccountsPage,
        meta: {
            title: 'Commercial Accounts',
            icon: 'material-symbols:apartment',
            sidebarGroup: 'commercial',
            sidebarOrder: 1,
            sidebarLabel: 'R1 Accounts',
        },
    },
    {
        name: 'deals',
        path: '/deals',
        component: DealsPage,
        meta: {
            title: 'Deals',
            icon: 'material-symbols:handshake-outline',
            sidebarGroup: 'commercial',
            sidebarOrder: 2,
        },
    },
    {
        name: 'deal-detail',
        path: '/deals/:dealId',
        component: DealDetailPage,
        meta: { title: 'Deal' },
    },
    {
        name: 'commercial-account-detail',
        path: '/commercial-accounts/:accountId',
        component: CommercialAccountDetailPage,
        meta: { title: 'Commercial Account' },
    },
    {
        name: 'tools-event-relays',
        path: '/tools/event-relays',
        component: EventRelaysPage,
        meta: {
            title: 'Event Relays',
            icon: 'material-symbols:sync-alt',
            sidebarGroup: 'developer',
            sidebarLabel: 'Event relays',
            sidebarOrder: 1,
        },
    },
    {
        name: 'tools-waste-stream-reporting',
        path: '/tools/waste-stream-reporting',
        component: WasteStreamReportPage,
        meta: {
            title: 'Waste Stream Report',
            reuseOnQueryChange: true,
            icon: 'material-symbols:recycling',
            sidebarGroup: 'post-job',
            sidebarOrder: 2,
        },
    },
    {
        name: 'lara-keys',
        path: '/lara/keys',
        component: LaraKeysPage,
        meta: {
            title: 'API keys',
            icon: 'material-symbols:key',
            sidebarGroup: 'lara',
            sidebarLabel: 'API keys',
            sidebarOrder: 1,
            requiresApplication: 'lara',
        },
    },
    {
        name: 'lara-aliases',
        path: '/lara/aliases',
        component: LaraAliasesPage,
        meta: {
            title: 'Model aliases',
            icon: 'material-symbols:alt-route',
            sidebarGroup: 'lara',
            sidebarLabel: 'Aliases',
            sidebarOrder: 2,
            requiresApplication: 'lara',
        },
    },
    {
        name: 'lara-prompts',
        path: '/lara/prompts',
        component: LaraPromptsPage,
        meta: {
            title: 'Prompts',
            icon: 'material-symbols:description',
            sidebarGroup: 'lara',
            sidebarLabel: 'Prompts',
            sidebarOrder: 3,
            requiresApplication: 'lara',
        },
    },
    {
        name: 'image-analysis',
        path: '/image-analysis',
        component: ImageAnalysisPage,
        meta: {
            title: 'Image analysis',
            icon: 'material-symbols:photo-camera',
            sidebarLabel: 'Image analysis',
            sidebarOrder: 10,
            requiresApplication: 'beehive',
        },
    },
    {
        path: '/lara/image-analysis',
        redirect: { name: 'image-analysis' },
    },
    {
        path: '/lara/vision/:rest(.*)*',
        redirect: { name: 'image-analysis' },
    },
    {
        name: 'unauthorized',
        path: '/unauthorized',
        component: UnauthorizedPage,
        meta: { title: 'Unauthorized Access' },
    },
    {
        name: 'supply',
        path: '/supply',
        component: SupplyPage,
        meta: {
            title: 'Supply',
            icon: 'material-symbols:local-shipping',
            sidebarGroup: 'supply-network',
            sidebarOrder: 0,
            sidebarLabel: 'Partners',
        },
    },
    {
        name: 'carrier-performance',
        path: '/supply/performance',
        component: CarrierPerformancePage,
        meta: {
            title: 'Carrier performance',
            icon: 'material-symbols:leaderboard',
            sidebarGroup: 'supply-network',
            sidebarOrder: 3,
            sidebarLabel: 'Performance',
            requiresApplication: 'beehive',
            requiresRole: ['admin', 'viewer'],
        },
    },
    {
        name: 'fast-track-partners',
        path: '/fast-track-partners',
        component: FastTrackPartnersPage,
        meta: {
            title: 'Fast Track Partners',
            icon: 'material-symbols:bolt',
            sidebarGroup: 'supply-network',
            sidebarOrder: 2,
            sidebarLabel: 'Fast Track',
        },
    },
    {
        name: 'fast-track-partner-create',
        path: '/fast-track-partners/new',
        component: FastTrackPartnerCreatePage,
        meta: {
            title: 'Add Fast Track Partner',
            requiresApplication: 'beehive',
            requiresRole: 'admin',
        },
    },
    {
        name: 'partner-applications',
        path: '/partner-applications',
        component: PartnerApplicationsPage,
        meta: {
            title: 'Partner Applications',
            icon: 'material-symbols:inbox',
            sidebarGroup: 'supply-network',
            sidebarOrder: 1,
            sidebarLabel: 'Applications',
            requiresApplication: 'beehive',
            requiresRole: 'admin',
        },
    },
    {
        name: 'partner-detail',
        path: '/partners/:partnerId',
        component: PartnerDetailPage,
        meta: {
            title: 'Partner',
            reuseOnQueryChange: true,
            requiresApplication: 'beehive',
            requiresRole: ['admin', 'viewer'],
        },
    },
    { path: '/:pathMatch(.*)*', redirect: '/' },
    {
        name: 'ui-debug',
        path: '/ui-debug',
        component: UiDebugPage,
        meta: { title: 'UI Debug' },
    },
    {
        name: 'developer-testing',
        path: '/developer-testing',
        component: DeveloperTesting,
        meta: { title: 'Developer Testing' },
    },
    {
        name: 'index-testing',
        path: '/index-testing',
        component: IndexTestingPage,
        meta: { title: 'Index Testing' },
    },
]

export const router = createRouter({
    history: createWebHistory(),
    routes,
})

const CHUNK_RELOAD_KEY = 'chunkReloadAt'

function reloadForStaleBuild(path: string): boolean {
    try {
        const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0)
        if (Date.now() - last < 10_000) return false
        sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()))
    } catch {
        // Without storage there is no loop guard, so do not reload.
        return false
    }
    window.location.assign(path)
    return true
}

const isChunkLoadError = (error: unknown): boolean =>
    error instanceof Error &&
    /dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(
        error.message,
    )

let navigatingTo: string | null = null

router.beforeEach(to => {
    navigatingTo = to.fullPath
})

const finishNavigation = (path: string) => {
    if (navigatingTo === path) navigatingTo = null
}

router.afterEach(to => {
    finishNavigation(to.fullPath)
})

router.onError((error, to) => {
    finishNavigation(to.fullPath)
    if (isChunkLoadError(error)) reloadForStaleBuild(to.fullPath)
})

// During a navigation the router's onError reloads to the page being opened;
// reloading here would land back on the page the user was leaving.
window.addEventListener('vite:preloadError', event => {
    if (navigatingTo !== null) return
    if (reloadForStaleBuild(window.location.href)) event.preventDefault()
})

router.beforeEach((to, from, next) => {
    if (to.meta.title) {
        document.title = to.meta.title
    }

    // Preserve staging=true query parameter across all routes
    if (from.query.staging === 'true' && !to.query.staging) {
        next({
            ...to,
            query: {
                ...to.query,
                staging: 'true',
            },
        })
        return
    }

    next()
})

/**
 * Enforces `requiresApplication` / `requiresRole` on direct navigation, since
 * hiding a nav entry is not access control. Roles come from POST /auth/me and
 * are cached, so this is a lookup rather than a request per navigation.
 *
 * The gateway checks the same thing again on every call. This only decides what
 * the browser will render.
 */
router.beforeEach(async to => {
    const requirements = requirementsForRoute(to.meta)
    if (requirements.length === 0) return true

    try {
        const { roles } = await fetchMeCached()
        return satisfies(requirements, roles) ? true : { name: 'unauthorized' }
    } catch (error) {
        // Cannot establish what they may do, so do not guess in their favour.
        return isTransientError(error) || error instanceof IdentityChangedError
            ? {
                  name: 'unauthorized',
                  query: { reason: 'unavailable', from: to.fullPath },
              }
            : { name: 'unauthorized' }
    }
})
